import React, { useState, useEffect, useMemo } from 'react';
import { useDashboard } from './DashboardContext';
import useVisiblePolling from '../../hooks/useVisiblePolling';
import {
  DollarSign,
  Target,
  TrendingUp,
  ClipboardCheck,
  X,
  FileText,
  ChevronDown,
  ChevronUp,
  Calendar,
  BarChart2,
  Award,
  AlertTriangle,
  Plus,
  Trash2
} from 'lucide-react';
import TradingCalendar from '../../components/TradingCalendar';
import DayDetailModal from '../../components/DayDetailModal';
import TradingPlanManager from '../../components/TradingPlanManager';
import HabitTrends from './HabitTrends';
import { tradingPlanApi, API } from '../../services/api';
import '../../components/TradingCalendar.css';

const PerformancePage = () => {
  const { 
    stats, 
    trades, 
    equityCurveData,
    showTradeModal,
    setShowTradeModal,
    editingTrade,
    setEditingTrade,
    tradeForm,
    setTradeForm,
    handleTradeFormChange,
    handleAddTrade,
    handleEditTrade,
    handleDeleteTrade,
    refreshTrades,
    defaultPairs,
    customPairs,
    newPairName,
    setNewPairName,
    handleAddCustomPair
  } = useDashboard();

  // Trading Plan State
  const [tradingPlanRules, setTradingPlanRules] = useState([]);
  const [habitLogs, setHabitLogs] = useState([]);
  const [todayHabitScore, setTodayHabitScore] = useState(null);
  const [monthlyHabitScore, setMonthlyHabitScore] = useState(null);
  const [isLoadingPlan, setIsLoadingPlan] = useState(false);
  
  // Trading Accounts State
  const [tradingAccounts, setTradingAccounts] = useState([]);
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(false);
  const [isSavingAccounts, setIsSavingAccounts] = useState(false);
  const [accountsSaved, setAccountsSaved] = useState(false);
  
  // Calendar/Modal State
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedDayTrades, setSelectedDayTrades] = useState(null);
  const [selectedDayHabitLog, setSelectedDayHabitLog] = useState(null);
  const [showDayModal, setShowDayModal] = useState(false);
  
  // Trade History State
  const [expandedHistoryNotes, setExpandedHistoryNotes] = useState({});
  const [expandedMonths, setExpandedMonths] = useState({});
  
  // Performance page tab: 'overview' (default) or 'trends'
  const [performanceTab, setPerformanceTab] = useState('overview');
  
  // Current month for habit logs
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1;
  
  // Filter trades for current month
  const monthlyTrades = useMemo(() => {
    return trades.filter(trade => {
      const tradeDate = trade.date?.split('T')[0];
      if (!tradeDate) return false;
      const [year, month] = tradeDate.split('-').map(Number);
      return year === currentYear && month === currentMonth;
    }).sort((a, b) => {
      // Sort by date descending (newest first)
      return new Date(b.date) - new Date(a.date);
    });
  }, [trades, currentYear, currentMonth]);

  // Calculate stats for a given set of trades
  const calculateStatsForTrades = (tradesList) => {
    if (!tradesList || tradesList.length === 0) {
      return {
        totalPL: 0,
        winRate: 0,
        wins: 0,
        losses: 0,
        avgRR: 0,
        totalTrades: 0,
        greenDays: 0,
        redDays: 0,
        bestTrade: null,
        worstTrade: null
      };
    }

    const totalPL = tradesList.reduce((sum, trade) => {
      const plValue = parseFloat(trade.profitLoss || 0);
      const adjustedPL = trade.outcome === 'Loss' ? -Math.abs(plValue) : Math.abs(plValue);
      return sum + adjustedPL;
    }, 0);

    const wins = tradesList.filter(t => t.outcome === 'Win').length;
    const losses = tradesList.filter(t => t.outcome === 'Loss').length;
    const totalTrades = wins + losses;
    const winRate = totalTrades > 0 ? ((wins / totalTrades) * 100).toFixed(1) : 0;

    let totalRR = 0;
    let rrCount = 0;
    tradesList.forEach(trade => {
      const entry = parseFloat(trade.entry || 0);
      const sl = parseFloat(trade.stopLoss || 0);
      const tp = parseFloat(trade.takeProfit || 0);
      if (entry && sl && tp) {
        const risk = Math.abs(entry - sl);
        const reward = Math.abs(tp - entry);
        if (risk > 0) {
          totalRR += reward / risk;
          rrCount++;
        }
      }
    });
    const avgRR = rrCount > 0 ? (totalRR / rrCount).toFixed(2) : 0;

    // Calculate green/red days
    const dayPL = {};
    tradesList.forEach(trade => {
      const date = trade.date?.split('T')[0];
      if (!date) return;
      const plValue = parseFloat(trade.profitLoss || 0);
      const adjustedPL = trade.outcome === 'Loss' ? -Math.abs(plValue) : Math.abs(plValue);
      dayPL[date] = (dayPL[date] || 0) + adjustedPL;
    });
    const greenDays = Object.values(dayPL).filter(pl => pl > 0).length;
    const redDays = Object.values(dayPL).filter(pl => pl < 0).length;

    // Find best and worst trades
    let bestTrade = null;
    let worstTrade = null;
    tradesList.forEach(trade => {
      const plValue = parseFloat(trade.profitLoss || 0);
      const adjustedPL = trade.outcome === 'Loss' ? -Math.abs(plValue) : Math.abs(plValue);
      
      // Best trade: highest positive P/L
      if (adjustedPL > 0 && (!bestTrade || adjustedPL > bestTrade.pl)) {
        bestTrade = { pl: adjustedPL, pair: trade.pair, date: trade.date?.split('T')[0] };
      }
      // Worst trade: only show if it's actually a loss (negative P/L)
      if (adjustedPL < 0 && (!worstTrade || adjustedPL < worstTrade.pl)) {
        worstTrade = { pl: adjustedPL, pair: trade.pair, date: trade.date?.split('T')[0] };
      }
    });

    return {
      totalPL: totalPL.toFixed(2),
      winRate,
      wins,
      losses,
      avgRR,
      totalTrades,
      greenDays,
      redDays,
      bestTrade,
      worstTrade
    };
  };

  // Current month stats (for top cards)
  const currentMonthStats = useMemo(() => {
    return calculateStatsForTrades(monthlyTrades);
  }, [monthlyTrades]);

  // Get all months in current year that have trades
  const monthlyPerformanceData = useMemo(() => {
    const monthsData = [];
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                        'July', 'August', 'September', 'October', 'November', 'December'];
    
    // Get trades for each month in the current year (excluding current month)
    for (let month = 1; month < currentMonth; month++) {
      const monthTrades = trades.filter(trade => {
        const tradeDate = trade.date?.split('T')[0];
        if (!tradeDate) return false;
        const [year, m] = tradeDate.split('-').map(Number);
        return year === currentYear && m === month;
      });
      
      if (monthTrades.length > 0) {
        const stats = calculateStatsForTrades(monthTrades);
        monthsData.push({
          month,
          year: currentYear,
          monthName: monthNames[month - 1],
          trades: monthTrades,
          stats
        });
      }
    }
    
    // Sort by month descending (most recent first)
    return monthsData.sort((a, b) => b.month - a.month);
  }, [trades, currentYear, currentMonth]);

  // Toggle month expansion
  const toggleMonthExpansion = (monthKey) => {
    setExpandedMonths(prev => ({
      ...prev,
      [monthKey]: !prev[monthKey]
    }));
  };
  
  // Toggle notes visibility in trade history
  const toggleHistoryNotes = (tradeId) => {
    setExpandedHistoryNotes(prev => ({
      ...prev,
      [tradeId]: !prev[tradeId]
    }));
  };
  
  // Load trading plan rules and habit logs
  useEffect(() => {
    loadTradingPlanData();
    loadTradingAccounts();
  }, []);

  // Poll for fresh trades + habit data every 20s while the tab is visible.
  // Picks up mobile-entered trades or habit logs without a manual refresh.
  useVisiblePolling(async () => {
    await Promise.all([
      refreshTrades ? refreshTrades() : Promise.resolve(),
      loadTradingPlanData(),
    ]);
  }, { intervalMs: 20000, deps: [currentYear, currentMonth] });
  
  // Helper to format number with commas for display
  const formatBalance = (value) => {
    if (value === null || value === undefined || value === '') return '';
    const num = typeof value === 'string' ? parseFloat(value.replace(/,/g, '')) : value;
    if (isNaN(num)) return value;
    return num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  };

  const loadTradingAccounts = async () => {
    setIsLoadingAccounts(true);
    try {
      const response = await fetch(`${API}/api/user/trading-accounts`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('hyperdriveToken')}` }
      });
      if (response.ok) {
        const data = await response.json();
        // Format balances with commas for display
        const formattedAccounts = (data.accounts || []).map(acc => ({
          ...acc,
          balance: formatBalance(acc.balance)
        }));
        setTradingAccounts(formattedAccounts);
      }
    } catch (error) {
      console.error('Failed to load trading accounts:', error);
    } finally {
      setIsLoadingAccounts(false);
    }
  };
  
  const saveTradingAccounts = async () => {
    setIsSavingAccounts(true);
    setAccountsSaved(false);
    try {
      // Convert balance strings to numbers before saving
      const accountsToSave = tradingAccounts.map(acc => ({
        ...acc,
        balance: typeof acc.balance === 'string' 
          ? parseFloat(acc.balance.replace(/,/g, '')) || 0 
          : (acc.balance || 0)
      }));
      const response = await fetch(`${API}/api/user/trading-accounts`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('hyperdriveToken')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ accounts: accountsToSave })
      });
      if (response.ok) {
        setAccountsSaved(true);
        // Update local state with formatted values for display
        const formattedAccounts = accountsToSave.map(acc => ({
          ...acc,
          balance: formatBalance(acc.balance)
        }));
        setTradingAccounts(formattedAccounts);
        setTimeout(() => setAccountsSaved(false), 3000);
      }
    } catch (error) {
      console.error('Failed to save trading accounts:', error);
    } finally {
      setIsSavingAccounts(false);
    }
  };
  
  const addTradingAccount = () => {
    const newAccount = {
      id: Date.now().toString(),
      host: '',
      type: 'Challenge',
      balance: 0
    };
    setTradingAccounts([...tradingAccounts, newAccount]);
  };
  
  const updateTradingAccount = (id, field, value) => {
    setTradingAccounts(tradingAccounts.map(acc => 
      acc.id === id ? { ...acc, [field]: value } : acc
    ));
  };
  
  const removeTradingAccount = (id) => {
    setTradingAccounts(tradingAccounts.filter(acc => acc.id !== id));
  };
  
  const loadTradingPlanData = async () => {
    try {
      // Load rules
      const rules = await tradingPlanApi.getRules();
      setTradingPlanRules(rules);
      
      // Load monthly habit logs
      const logs = await tradingPlanApi.getMonthlyHabitLogs(currentYear, currentMonth);
      setHabitLogs(logs);
      
      // Get today's habit log
      const today = `${currentYear}-${String(currentMonth).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`;
      const todayLog = logs.find(log => log.date === today);
      if (todayLog) {
        setTodayHabitScore(todayLog.score);
      }
      
      // Get monthly stats
      const monthlyStats = await tradingPlanApi.getMonthlyStats(currentYear, currentMonth);
      if (monthlyStats && monthlyStats.average_score !== null) {
        setMonthlyHabitScore(monthlyStats.average_score);
      }
    } catch (error) {
      console.error('Failed to load trading plan data:', error);
    }
  };
  
  // Recalculate habit scores based on CURRENT rules
  // This ensures scores update when rules are added/deleted
  const recalculatedHabitScores = useMemo(() => {
    if (!tradingPlanRules || tradingPlanRules.length === 0 || !habitLogs || habitLogs.length === 0) {
      return { todayScore: null, monthlyScore: null };
    }
    
    const currentRuleIds = new Set(tradingPlanRules.map(r => r.id));
    const totalRules = tradingPlanRules.length;
    const today = `${currentYear}-${String(currentMonth).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`;
    
    let todayScore = null;
    let validDayScores = [];
    
    habitLogs.forEach(log => {
      if (!log.entries || log.entries.length === 0) return;
      
      // Filter entries to only include current rules
      const relevantEntries = log.entries.filter(entry => currentRuleIds.has(entry.rule_id));
      
      // If no entries match current rules, skip this day
      if (relevantEntries.length === 0) return;
      
      // Count how many current rules were followed
      const followedCount = relevantEntries.filter(e => e.followed).length;
      
      // Recalculate score based on TOTAL current rules (not just matched entries)
      const recalculatedScore = (followedCount / totalRules) * 100;
      
      // Only count days where at least one habit was followed (score > 0)
      if (recalculatedScore > 0) {
        validDayScores.push(recalculatedScore);
      }
      
      // Check if this is today
      if (log.date === today) {
        todayScore = recalculatedScore;
      }
    });
    
    // Calculate monthly average from valid days only
    const monthlyScore = validDayScores.length > 0 
      ? validDayScores.reduce((sum, s) => sum + s, 0) / validDayScores.length
      : null;
    
    return { todayScore, monthlyScore };
  }, [habitLogs, tradingPlanRules, currentYear, currentMonth, currentDate]);

  // Trading Plan Rule handlers
  const handleAddRule = async (ruleText) => {
    setIsLoadingPlan(true);
    try {
      const newRule = await tradingPlanApi.createRule(ruleText);
      setTradingPlanRules(prev => [...prev, newRule]);
    } catch (error) {
      console.error('Failed to add rule:', error);
      alert('Failed to add rule. Please try again.');
    } finally {
      setIsLoadingPlan(false);
    }
  };
  
  const handleUpdateRule = async (ruleId, updates) => {
    try {
      const updatedRule = await tradingPlanApi.updateRule(ruleId, updates);
      setTradingPlanRules(prev => prev.map(r => r.id === ruleId ? updatedRule : r));
    } catch (error) {
      console.error('Failed to update rule:', error);
      alert('Failed to update rule. Please try again.');
    }
  };
  
  const handleDeleteRule = async (ruleId) => {
    if (!window.confirm('Are you sure you want to delete this rule?')) return;
    try {
      await tradingPlanApi.deleteRule(ruleId);
      setTradingPlanRules(prev => prev.filter(r => r.id !== ruleId));
    } catch (error) {
      console.error('Failed to delete rule:', error);
      alert('Failed to delete rule. Please try again.');
    }
  };
  
  const handleReorderRules = async (ruleOrders) => {
    // Optimistic update
    const reorderedRules = [...tradingPlanRules].sort((a, b) => {
      const orderA = ruleOrders.find(o => o.id === a.id)?.order || 0;
      const orderB = ruleOrders.find(o => o.id === b.id)?.order || 0;
      return orderA - orderB;
    });
    setTradingPlanRules(reorderedRules);
    
    try {
      await tradingPlanApi.reorderRules(ruleOrders);
    } catch (error) {
      console.error('Failed to reorder rules:', error);
      loadTradingPlanData();
    }
  };
  
  // Day modal handlers
  const handleDayClick = (date, dayTrades, habitLog) => {
    setSelectedDate(date);
    setSelectedDayTrades(dayTrades?.trades || []);
    setSelectedDayHabitLog(habitLog || null);
    setShowDayModal(true);
  };
  
  const handleCloseDayModal = () => {
    setShowDayModal(false);
    setSelectedDate(null);
    setSelectedDayTrades(null);
    setSelectedDayHabitLog(null);
  };
  
  // Update selectedDayTrades when trades change (after add/edit/delete)
  useEffect(() => {
    if (selectedDate && showDayModal) {
      const dayTrades = trades.filter(t => {
        const tradeDate = t.date?.split('T')[0];
        return tradeDate === selectedDate;
      });
      setSelectedDayTrades(dayTrades);
    }
  }, [trades, selectedDate, showDayModal]);
  
  const handleAddTradeFromModal = (date) => {
    setTradeForm(prev => ({ ...prev, date }));
    setEditingTrade(null);
    setShowTradeModal(true);
  };
  
  const handleEditTradeFromModal = (trade) => {
    handleEditTrade(trade);
  };
  
  const handleDeleteTradeFromModal = async (tradeId) => {
    if (!window.confirm('Are you sure you want to delete this trade?')) return;
    await handleDeleteTrade(tradeId);
    setSelectedDayTrades(prev => prev.filter(t => t.id !== tradeId));
  };
  
  const handleSaveHabitLog = async (date, entries) => {
    try {
      const result = await tradingPlanApi.saveHabitLog(date, entries);
      
      const newLog = { date, entries, score: result.score };
      setHabitLogs(prev => {
        const existing = prev.findIndex(l => l.date === date);
        if (existing >= 0) {
          const updated = [...prev];
          updated[existing] = newLog;
          return updated;
        }
        return [...prev, newLog];
      });
      
      const today = `${currentYear}-${String(currentMonth).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`;
      if (date === today) {
        setTodayHabitScore(result.score);
      }
      
      const monthlyStats = await tradingPlanApi.getMonthlyStats(currentYear, currentMonth);
      if (monthlyStats && monthlyStats.average_score !== null) {
        setMonthlyHabitScore(monthlyStats.average_score);
      }
      
      alert('Habit checklist saved!');
    } catch (error) {
      console.error('Failed to save habit log:', error);
      alert('Failed to save habit log. Please try again.');
    }
  };

  // Get current month name for display
  const currentMonthName = new Date(currentYear, currentMonth - 1).toLocaleString('default', { month: 'long' });

  const performanceStats = [
    {
      icon: DollarSign,
      label: 'Total P&L',
      value: `$${currentMonthStats.totalPL >= 0 ? '' : '-'}${Math.abs(currentMonthStats.totalPL).toLocaleString()}`,
      color: currentMonthStats.totalPL >= 0 ? '#10b981' : '#ef4444',
      iconBg: currentMonthStats.totalPL >= 0 ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
    },
    {
      icon: Target,
      label: 'Win Rate',
      value: `${currentMonthStats.winRate}%`,
      subtitle: `${currentMonthStats.wins}W / ${currentMonthStats.losses}L`,
      color: '#ef4444',
      iconBg: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
    },
    {
      icon: TrendingUp,
      label: 'Avg R:R',
      value: `${currentMonthStats.avgRR}R`,
      color: '#8b5cf6',
      iconBg: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)'
    },
    {
      icon: ClipboardCheck,
      label: 'Habit Score',
      value: recalculatedHabitScores.todayScore !== null || recalculatedHabitScores.monthlyScore !== null 
        ? null
        : 'Not tracked',
      customValue: (recalculatedHabitScores.todayScore !== null || recalculatedHabitScores.monthlyScore !== null) ? (
        <div className="habit-score-value">
          <div className="habit-today-score">
            <span className="habit-score-label">Today:</span>
            <span>{recalculatedHabitScores.todayScore !== null ? `${Number(recalculatedHabitScores.todayScore).toFixed(1)}%` : '-'}</span>
          </div>
          <div className="habit-month-score">
            <span className="habit-score-label">Month:</span>
            <span>{recalculatedHabitScores.monthlyScore !== null ? `${Number(recalculatedHabitScores.monthlyScore).toFixed(1)}%` : '-'}</span>
          </div>
        </div>
      ) : null,
      color: '#8b5cf6',
      iconBg: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
      isHabitCard: true
    },
  ];
  
  // Time formatting helper - converts "943" to "9:43"
  const formatTimeInput = (value) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');
    
    if (digits.length === 0) return '';
    if (digits.length <= 2) return digits;
    if (digits.length === 3) return `${digits[0]}:${digits.slice(1)}`;
    if (digits.length >= 4) return `${digits.slice(0, 2)}:${digits.slice(2, 4)}`;
    return value;
  };
  
  // Handle time input change with formatting
  const handleTimeChange = (e) => {
    const formatted = formatTimeInput(e.target.value);
    // Extract current AM/PM or default to AM
    const currentTime = tradeForm.time || '';
    const currentPeriod = currentTime.includes('PM') ? 'PM' : 'AM';
    const newTime = formatted ? `${formatted} ${currentPeriod}` : '';
    setTradeForm(prev => ({ ...prev, time: newTime }));
  };
  
  // Handle AM/PM toggle
  const handlePeriodChange = (period) => {
    const currentTime = tradeForm.time || '';
    const timeWithoutPeriod = currentTime.replace(/\s*(AM|PM)\s*/i, '').trim();
    const newTime = timeWithoutPeriod ? `${timeWithoutPeriod} ${period}` : '';
    setTradeForm(prev => ({ ...prev, time: newTime }));
  };
  
  // Get time parts from tradeForm.time
  const getTimeParts = () => {
    const time = tradeForm.time || '';
    const timeWithoutPeriod = time.replace(/\s*(AM|PM)\s*/i, '').trim();
    const period = time.includes('PM') ? 'PM' : 'AM';
    return { timeValue: timeWithoutPeriod, period };
  };

  // Chart calculations
  const chartWidth = 900;
  const chartHeight = 320;
  const chartPadding = { top: 40, right: 40, bottom: 60, left: 70 };
  const innerWidth = chartWidth - chartPadding.left - chartPadding.right;
  const innerHeight = chartHeight - chartPadding.top - chartPadding.bottom;

  const yValues = equityCurveData.length > 0 ? equityCurveData.map(d => d.value) : [0];
  const dataMax = Math.max(...yValues, 0);
  const dataMin = Math.min(...yValues, 0);
  const yPadding = Math.max(Math.abs(dataMax), Math.abs(dataMin), 500) * 0.2;
  const maxY = Math.ceil((dataMax + yPadding) / 500) * 500;
  const minY = Math.floor((dataMin - yPadding) / 500) * 500;
  const yRange = maxY - minY || 1000;

  const yTickCount = 5;
  const yTickStep = yRange / (yTickCount - 1);
  const yTicks = Array.from({ length: yTickCount }, (_, i) => maxY - i * yTickStep);

  const getX = (index) => chartPadding.left + (index / Math.max(equityCurveData.length - 1, 1)) * innerWidth;
  const getY = (value) => chartPadding.top + innerHeight - ((value - minY) / yRange) * innerHeight;

  const generateSmoothPath = (points) => {
    if (points.length < 2) return '';
    if (points.length === 2) {
      return `M ${points[0].x} ${points[0].y} L ${points[1].x} ${points[1].y}`;
    }
    let path = `M ${points[0].x} ${points[0].y}`;
    for (let i = 0; i < points.length - 1; i++) {
      const p0 = points[Math.max(0, i - 1)];
      const p1 = points[i];
      const p2 = points[i + 1];
      const p3 = points[Math.min(points.length - 1, i + 2)];
      const tension = 0.3;
      const cp1x = p1.x + (p2.x - p0.x) * tension;
      const cp1y = p1.y + (p2.y - p0.y) * tension;
      const cp2x = p2.x - (p3.x - p1.x) * tension;
      const cp2y = p2.y - (p3.y - p1.y) * tension;
      path += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`;
    }
    return path;
  };

  const curvePoints = equityCurveData.map((d, i) => ({
    x: getX(i),
    y: getY(d.value)
  }));

  const smoothLinePath = generateSmoothPath(curvePoints);
  const smoothAreaPath = equityCurveData.length > 1
    ? smoothLinePath + ` L ${getX(equityCurveData.length - 1)} ${getY(0)} L ${getX(0)} ${getY(0)} Z`
    : '';

  const finalPL = equityCurveData[equityCurveData.length - 1]?.value || 0;
  const curveColor = finalPL >= 0 ? '#22c55e' : '#ef4444';

  return (
    <div className="page-content">
      <h1 className="page-title">Performance Analytics</h1>
      <p className="page-description">Track your trades, monitor your discipline, and analyze your performance</p>
      
      {/* Tabs: Overview vs Trends */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <button
          data-testid="performance-tab-overview"
          onClick={() => setPerformanceTab('overview')}
          style={{
            padding: '10px 18px',
            background: 'transparent',
            border: 'none',
            color: performanceTab === 'overview' ? '#fff' : '#9ca3af',
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
            borderBottom: performanceTab === 'overview' ? '2px solid #ef4444' : '2px solid transparent',
            marginBottom: -1,
          }}
        >Overview</button>
        <button
          data-testid="performance-tab-trends"
          onClick={() => setPerformanceTab('trends')}
          style={{
            padding: '10px 18px',
            background: 'transparent',
            border: 'none',
            color: performanceTab === 'trends' ? '#fff' : '#9ca3af',
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
            borderBottom: performanceTab === 'trends' ? '2px solid #ef4444' : '2px solid transparent',
            marginBottom: -1,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        ><BarChart2 size={14} /> Trends</button>
      </div>

      {performanceTab === 'trends' ? (
        <HabitTrends />
      ) : (
      <>
      {/* 1. Stats Cards - Current Month */}
      <div className="current-month-stats-header">
        <Calendar size={20} />
        <h3>{currentMonthName} {currentYear} Performance</h3>
      </div>
      <div className="stats-grid-full">
        {performanceStats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div 
              key={index} 
              className={`stat-card-new ${stat.isHabitCard ? 'habit-score-card' : ''}`}
            >
              <div className="stat-card-content">
                <div className="stat-header">
                  <span className="stat-label-new">{stat.label}</span>
                  <div className="stat-icon-new" style={{ background: stat.iconBg }}>
                    <Icon size={18} />
                  </div>
                </div>
                {stat.customValue ? (
                  <div className="stat-value-new" style={{ color: stat.color }}>
                    {stat.customValue}
                  </div>
                ) : (
                  <div className="stat-value-new" style={{ color: stat.color }}>
                    {stat.value}
                  </div>
                )}
                {stat.subtitle && (
                  <div className="stat-subtitle-new">{stat.subtitle}</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      
      {/* 2. Trading Plan Manager */}
      <TradingPlanManager
        rules={tradingPlanRules}
        onAddRule={handleAddRule}
        onUpdateRule={handleUpdateRule}
        onDeleteRule={handleDeleteRule}
        onReorderRules={handleReorderRules}
        isLoading={isLoadingPlan}
      />
      
      {/* 2.5 Trading Accounts */}
      <div className="trading-accounts-card">
        <h3 className="card-section-title">Trading Accounts</h3>
        {isLoadingAccounts ? (
          <div className="loading-state">Loading accounts...</div>
        ) : (
          <>
            {tradingAccounts.length === 0 ? (
              <div className="empty-accounts-state">
                <p>No trading accounts added yet. Add your prop firm or brokerage accounts to track them.</p>
              </div>
            ) : (
              <div className="trading-accounts-list">
                {tradingAccounts.map((account) => (
                  <div key={account.id} className="trading-account-row">
                    <div className="account-field">
                      <label>Host</label>
                      <input
                        type="text"
                        placeholder="e.g. TopStep, TopOne, Apex"
                        value={account.host}
                        onChange={(e) => updateTradingAccount(account.id, 'host', e.target.value)}
                      />
                    </div>
                    <div className="account-field">
                      <label>Type</label>
                      <select
                        value={account.type}
                        onChange={(e) => updateTradingAccount(account.id, 'type', e.target.value)}
                      >
                        <option value="Challenge">Challenge</option>
                        <option value="Funded">Funded</option>
                        <option value="Live">Live</option>
                      </select>
                    </div>
                    <div className="account-field">
                      <label>Balance</label>
                      <input
                        type="text"
                        inputMode="decimal"
                        placeholder="0.00"
                        value={account.balance}
                        onChange={(e) => {
                          // Allow numbers, commas, decimal point, and minus sign for negative balances
                          const value = e.target.value.replace(/[^0-9.,-]/g, '');
                          updateTradingAccount(account.id, 'balance', value);
                        }}
                      />
                    </div>
                    <button 
                      className="remove-account-btn"
                      onClick={() => removeTradingAccount(account.id)}
                      title="Remove account"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="trading-accounts-actions">
              <button className="add-account-btn" onClick={addTradingAccount}>
                <Plus size={16} /> Add Account
              </button>
              {tradingAccounts.length > 0 && (
                <button 
                  className={`save-accounts-btn ${accountsSaved ? 'saved' : ''}`}
                  onClick={saveTradingAccounts}
                  disabled={isSavingAccounts}
                >
                  {isSavingAccounts ? 'Saving...' : accountsSaved ? '✓ Saved' : 'Save'}
                </button>
              )}
            </div>
          </>
        )}
      </div>
      
      {/* 3. Trading Calendar */}
      <TradingCalendar
        trades={trades}
        habitLogs={habitLogs}
        tradingPlanRules={tradingPlanRules}
        onDayClick={handleDayClick}
        selectedDate={selectedDate}
      />
      
      {/* 4. Equity Curve - Monthly */}
      <div className="equity-curve-card-full">
        <h3 className="card-section-title">{currentMonthName} Equity Curve</h3>
        {equityCurveData.length === 0 ? (
          <div className="empty-state-large">
            <TrendingUp size={48} className="empty-icon" />
            <p className="empty-message">No trade data for {currentMonthName} yet. Start logging trades to see your equity curve!</p>
          </div>
        ) : (
          <div className="equity-chart-container-full">
            <svg className="equity-chart-full" viewBox={`0 0 ${chartWidth} ${chartHeight}`} preserveAspectRatio="none">
              <defs>
                <linearGradient id="areaGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor={curveColor} stopOpacity="0.4" />
                  <stop offset="50%" stopColor={curveColor} stopOpacity="0.15" />
                  <stop offset="100%" stopColor={curveColor} stopOpacity="0" />
                </linearGradient>
                <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
                  <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
                  <feMerge>
                    <feMergeNode in="coloredBlur"/>
                    <feMergeNode in="SourceGraphic"/>
                  </feMerge>
                </filter>
              </defs>
              
              <rect x={chartPadding.left} y={chartPadding.top} width={innerWidth} height={innerHeight} fill="#0f1419" rx="4" />
              
              {yTicks.map((tick, i) => (
                <line 
                  key={i}
                  x1={chartPadding.left} 
                  y1={getY(tick)} 
                  x2={chartPadding.left + innerWidth} 
                  y2={getY(tick)} 
                  stroke="#2d3748" 
                  strokeWidth="1" 
                  strokeDasharray="4 4"
                />
              ))}
              
              {equityCurveData.map((d, i) => (
                <line 
                  key={i}
                  x1={getX(i)} 
                  y1={chartPadding.top} 
                  x2={getX(i)} 
                  y2={chartPadding.top + innerHeight} 
                  stroke="#2d3748" 
                  strokeWidth="1" 
                  strokeDasharray="4 4"
                />
              ))}
              
              {yTicks.map((tick, i) => (
                <text 
                  key={i}
                  x={chartPadding.left - 10} 
                  y={getY(tick) + 4} 
                  fill="#707a8a" 
                  fontSize="11" 
                  textAnchor="end"
                  fontFamily="system-ui, -apple-system, sans-serif"
                >
                  {tick}
                </text>
              ))}
              
              {equityCurveData.map((d, i) => (
                <text 
                  key={i}
                  x={getX(i)} 
                  y={chartPadding.top + innerHeight + 25} 
                  fill="#707a8a" 
                  fontSize="11" 
                  textAnchor="middle"
                  fontFamily="system-ui, -apple-system, sans-serif"
                >
                  {i + 1}
                </text>
              ))}
              
              <g transform={`translate(${chartPadding.left + innerWidth / 2}, ${chartHeight - 10})`}>
                <circle cx="-30" cy="0" r="4" fill={curveColor} />
                <text x="-20" y="4" fill="#707a8a" fontSize="11" fontFamily="system-ui, -apple-system, sans-serif">P&L ($)</text>
              </g>
              
              <path d={smoothAreaPath} fill="url(#areaGradient)" />
              
              <path 
                d={smoothLinePath} 
                fill="none" 
                stroke={curveColor} 
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                filter="url(#glow)"
              />
              
              {equityCurveData.map((d, i) => (
                <g key={i}>
                  <circle cx={getX(i)} cy={getY(d.value)} r="8" fill="none" stroke={curveColor} strokeWidth="2" opacity="0.3" />
                  <circle cx={getX(i)} cy={getY(d.value)} r="5" fill="#0f1419" stroke={curveColor} strokeWidth="2.5" />
                </g>
              ))}
            </svg>
            
            <div className="equity-summary-full">
              <div className="equity-stat">
                <span className="equity-stat-label">{currentMonthName} P&L</span>
                <span className={`equity-stat-value ${equityCurveData[equityCurveData.length - 1]?.value >= 0 ? 'positive' : 'negative'}`}>
                  ${equityCurveData[equityCurveData.length - 1]?.value?.toFixed(2) || '0.00'}
                </span>
              </div>
              <div className="equity-stat">
                <span className="equity-stat-label">{currentMonthName} Trades</span>
                <span className="equity-stat-value">{monthlyTrades.length}</span>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* 5. Trade History (View Only) */}
      <div className="trade-history-section">
        <div className="trade-history-header">
          <h3 className="card-section-title">
            Trade History - {new Date(currentYear, currentMonth - 1).toLocaleString('default', { month: 'long' })} {currentYear}
          </h3>
          <span className="trade-history-count">{monthlyTrades.length} trades</span>
        </div>
        
        {monthlyTrades.length === 0 ? (
          <div className="empty-state-large">
            <p className="empty-message">No trades logged this month. Click on a day in the calendar to add your first trade!</p>
          </div>
        ) : (
          <div className="trade-history-table-wrapper">
            <table className="trade-history-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Pair</th>
                  <th>Direction</th>
                  <th>Time</th>
                  <th>Session</th>
                  <th>Entry</th>
                  <th>Exit</th>
                  <th>P/L</th>
                  <th>Outcome</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {monthlyTrades.map(trade => (
                  <React.Fragment key={trade.id}>
                    <tr className={expandedHistoryNotes[trade.id] ? 'notes-expanded-parent' : ''}>
                      <td>{trade.date?.split('T')[0]}</td>
                      <td className="pair-cell">{trade.pair}</td>
                      <td>
                        <span className={`direction-badge ${trade.direction?.toLowerCase()}`}>
                          {trade.direction}
                        </span>
                      </td>
                      <td>{trade.time || '-'}</td>
                      <td>{trade.session}</td>
                      <td>{trade.entry}</td>
                      <td>{trade.outcome === 'Win' ? trade.takeProfit : trade.stopLoss}</td>
                      <td className={`pl-cell ${trade.outcome?.toLowerCase() === 'win' ? 'positive' : trade.outcome?.toLowerCase() === 'loss' ? 'negative' : ''}`}>
                        ${Math.abs(parseFloat(trade.profitLoss) || 0).toFixed(2)}
                      </td>
                      <td>
                        <span className={`outcome-badge ${trade.outcome?.toLowerCase()}`}>
                          {trade.outcome}
                        </span>
                      </td>
                      <td>
                        {trade.notes && trade.notes.trim() ? (
                          <button 
                            className={`notes-view-btn ${expandedHistoryNotes[trade.id] ? 'active' : ''}`}
                            onClick={() => toggleHistoryNotes(trade.id)}
                            title={expandedHistoryNotes[trade.id] ? 'Hide notes' : 'View notes'}
                          >
                            <FileText size={16} />
                            {expandedHistoryNotes[trade.id] ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                          </button>
                        ) : (
                          <span className="no-notes">-</span>
                        )}
                      </td>
                    </tr>
                    {trade.notes && trade.notes.trim() && expandedHistoryNotes[trade.id] && (
                      <tr className="notes-expanded-row">
                        <td colSpan="10">
                          <div className="trade-notes-content">
                            <FileText size={16} className="notes-icon" />
                            <div className="notes-text">{trade.notes}</div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* 6. Monthly Performance History */}
      {monthlyPerformanceData.length > 0 && (
        <div className="monthly-performance-history">
          <div className="monthly-history-header">
            <BarChart2 size={20} />
            <h3>Past Months Performance - {currentYear}</h3>
          </div>
          
          <div className="monthly-history-list">
            {monthlyPerformanceData.map((monthData) => {
              const monthKey = `${monthData.year}-${monthData.month}`;
              const isExpanded = expandedMonths[monthKey];
              const plValue = parseFloat(monthData.stats.totalPL);
              const isPositive = plValue >= 0;
              
              return (
                <div key={monthKey} className={`month-performance-card ${isExpanded ? 'expanded' : ''}`}>
                  <button 
                    className="month-header-btn"
                    onClick={() => toggleMonthExpansion(monthKey)}
                  >
                    <div className="month-header-left">
                      <span className="month-name">{monthData.monthName} {monthData.year}</span>
                      <span className="month-trade-count">{monthData.stats.totalTrades} trades</span>
                    </div>
                    <div className="month-header-right">
                      <span className={`month-pl ${isPositive ? 'positive' : 'negative'}`}>
                        {isPositive ? '+' : ''}${plValue.toLocaleString()}
                      </span>
                      {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                    </div>
                  </button>
                  
                  {isExpanded && (
                    <div className="month-details">
                      {/* Stats Cards Row */}
                      <div className="month-stats-grid">
                        <div className="month-stat-card">
                          <DollarSign size={16} />
                          <div className="month-stat-info">
                            <span className="month-stat-label">Total P&L</span>
                            <span className={`month-stat-value ${isPositive ? 'positive' : 'negative'}`}>
                              ${Math.abs(plValue).toLocaleString()}
                            </span>
                          </div>
                        </div>
                        <div className="month-stat-card">
                          <Target size={16} />
                          <div className="month-stat-info">
                            <span className="month-stat-label">Win Rate</span>
                            <span className="month-stat-value">{monthData.stats.winRate}%</span>
                            <span className="month-stat-subtitle">{monthData.stats.wins}W / {monthData.stats.losses}L</span>
                          </div>
                        </div>
                        <div className="month-stat-card">
                          <TrendingUp size={16} />
                          <div className="month-stat-info">
                            <span className="month-stat-label">Avg R:R</span>
                            <span className="month-stat-value">{monthData.stats.avgRR}R</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Additional Stats Row */}
                      <div className="month-extra-stats">
                        <div className="extra-stat">
                          <span className="extra-stat-label">Trading Days</span>
                          <div className="trading-days-display">
                            <span className="green-days">{monthData.stats.greenDays} Green</span>
                            <span className="day-separator">/</span>
                            <span className="red-days">{monthData.stats.redDays} Red</span>
                          </div>
                        </div>
                        
                        {monthData.stats.bestTrade && (
                          <div className="extra-stat">
                            <span className="extra-stat-label">
                              <Award size={14} /> Best Trade
                            </span>
                            <span className="extra-stat-value positive">
                              +${Math.abs(monthData.stats.bestTrade.pl).toFixed(2)} ({monthData.stats.bestTrade.pair})
                            </span>
                          </div>
                        )}
                        
                        {monthData.stats.worstTrade && (
                          <div className="extra-stat">
                            <span className="extra-stat-label">
                              <AlertTriangle size={14} /> Worst Trade
                            </span>
                            <span className="extra-stat-value negative">
                              -${Math.abs(monthData.stats.worstTrade.pl).toFixed(2)} ({monthData.stats.worstTrade.pair})
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
      </>
      )}
      
      {/* Day Detail Modal */}
      <DayDetailModal
        isOpen={showDayModal}
        onClose={handleCloseDayModal}
        date={selectedDate}
        trades={selectedDayTrades}
        habitLog={selectedDayHabitLog}
        tradingPlanRules={tradingPlanRules}
        onAddTrade={handleAddTradeFromModal}
        onEditTrade={handleEditTradeFromModal}
        onDeleteTrade={handleDeleteTradeFromModal}
        onSaveHabitLog={handleSaveHabitLog}
      />
      
      {/* Trade Modal */}
      {showTradeModal && (
        <div className="modal-overlay" onClick={() => { setShowTradeModal(false); setEditingTrade(null); }}>
          <div className="modal-content trade-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{editingTrade ? 'Edit Trade' : 'Add New Trade'}</h2>
              <button className="modal-close" onClick={() => { setShowTradeModal(false); setEditingTrade(null); }}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="modal-form-grid">
                <div className="form-group-modal">
                  <label>Trade Date</label>
                  <input 
                    type="date" 
                    name="date" 
                    value={tradeForm.date} 
                    onChange={handleTradeFormChange}
                    className="date-picker-input"
                  />
                </div>
                <div className="form-group-modal">
                  <label>Pair</label>
                  <select name="pair" value={tradeForm.pair} onChange={handleTradeFormChange}>
                    <option value="">Select a pair</option>
                    {defaultPairs.map(pair => (
                      <option key={pair} value={pair}>{pair}</option>
                    ))}
                    {customPairs.map(pair => (
                      <option key={pair} value={pair}>{pair}</option>
                    ))}
                    <option value="__add_new__">+ Add New Pair</option>
                  </select>
                  {tradeForm.pair === '__add_new__' && (
                    <div className="add-pair-inline">
                      <input 
                        type="text" 
                        placeholder="Enter pair name (e.g., BTCUSD)"
                        value={newPairName}
                        onChange={(e) => setNewPairName(e.target.value.toUpperCase())}
                        onKeyPress={async (e) => {
                          if (e.key === 'Enter' && newPairName.trim()) {
                            const pairToAdd = newPairName.trim().toUpperCase();
                            await handleAddCustomPair(pairToAdd);
                            setTradeForm(prev => ({...prev, pair: pairToAdd}));
                          }
                        }}
                      />
                      <button 
                        className="add-pair-btn"
                        onClick={async () => {
                          if (newPairName.trim()) {
                            const pairToAdd = newPairName.trim().toUpperCase();
                            await handleAddCustomPair(pairToAdd);
                            setTradeForm(prev => ({...prev, pair: pairToAdd}));
                          }
                        }}
                      >
                        Add
                      </button>
                    </div>
                  )}
                </div>
                <div className="form-group-modal">
                  <label>Direction</label>
                  <select name="direction" value={tradeForm.direction} onChange={handleTradeFormChange}>
                    <option value="Long">Long</option>
                    <option value="Short">Short</option>
                  </select>
                </div>
                <div className="form-group-modal">
                  <label>Session</label>
                  <select name="session" value={tradeForm.session} onChange={handleTradeFormChange}>
                    <option value="New York">New York</option>
                    <option value="London">London</option>
                    <option value="Tokyo">Tokyo</option>
                    <option value="Sydney">Sydney</option>
                  </select>
                </div>
                <div className="form-group-modal">
                  <label>Time</label>
                  <div className="time-input-group">
                    <input 
                      type="text" 
                      name="time" 
                      value={getTimeParts().timeValue} 
                      onChange={handleTimeChange} 
                      placeholder="9:43"
                      maxLength={5}
                      className="time-input"
                    />
                    <div className="time-period-toggle">
                      <button 
                        type="button"
                        className={`period-btn ${getTimeParts().period === 'AM' ? 'active' : ''}`}
                        onClick={() => handlePeriodChange('AM')}
                      >
                        AM
                      </button>
                      <button 
                        type="button"
                        className={`period-btn ${getTimeParts().period === 'PM' ? 'active' : ''}`}
                        onClick={() => handlePeriodChange('PM')}
                      >
                        PM
                      </button>
                    </div>
                  </div>
                </div>
                <div className="form-group-modal">
                  <label>Outcome</label>
                  <select name="outcome" value={tradeForm.outcome} onChange={handleTradeFormChange}>
                    <option value="Win">Win</option>
                    <option value="Loss">Loss</option>
                    <option value="Breakeven">Breakeven</option>
                  </select>
                </div>
                <div className="form-group-modal">
                  <label>Entry</label>
                  <input type="text" inputMode="decimal" name="entry" value={tradeForm.entry} onChange={handleTradeFormChange} placeholder="0.00" />
                </div>
                <div className="form-group-modal">
                  <label>Stop Loss</label>
                  <input type="text" inputMode="decimal" name="stopLoss" value={tradeForm.stopLoss} onChange={handleTradeFormChange} placeholder="0.00" />
                </div>
                <div className="form-group-modal">
                  <label>Take Profit</label>
                  <input type="text" inputMode="decimal" name="takeProfit" value={tradeForm.takeProfit} onChange={handleTradeFormChange} placeholder="0.00" />
                </div>
                <div className="form-group-modal">
                  <label>Lot Size</label>
                  <input type="text" inputMode="decimal" name="lotSize" value={tradeForm.lotSize} onChange={handleTradeFormChange} placeholder="0.00" />
                </div>
                <div className="form-group-modal">
                  <label>Pips/Points</label>
                  <input type="text" inputMode="decimal" name="pipsPoints" value={tradeForm.pipsPoints} onChange={handleTradeFormChange} placeholder="0" />
                </div>
                <div className="form-group-modal">
                  <label>Profit/Loss ($)</label>
                  <input type="text" inputMode="decimal" name="profitLoss" value={tradeForm.profitLoss} onChange={handleTradeFormChange} placeholder="0.00" />
                </div>
                <div className="form-group-modal full-width">
                  <label>Notes</label>
                  <textarea 
                    name="notes" 
                    value={tradeForm.notes} 
                    onChange={handleTradeFormChange}
                    rows={3}
                    placeholder="Add notes about this trade..."
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => { setShowTradeModal(false); setEditingTrade(null); }}>
                Cancel
              </button>
              <button className="btn-primary" onClick={handleAddTrade}>
                {editingTrade ? 'Update Trade' : 'Add Trade'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PerformancePage;
